import type { MacroWalkResponsePayload } from '@playcastdotio/Messaging';
import type { GameLaunchCoordinator } from '../../game-launch/GameLaunchCoordinator';
import type { PavilionCoordinator } from '../../pavilion/PavilionCoordinator';
import * as Sentry from '@sentry/react';

export const MACRO_WALK_MAX_RETRIES = 3;
export const RESTART_GAMECLOSED_TIMEOUT_MS = 10_000;
export const GAME_LAUNCHED_REQUEUE_TIMEOUT_MS = 5_000;

export interface MacroWalkRecoveryDeps {
  gameLaunchCoordinator: GameLaunchCoordinator;
  pavilionCoordinator: PavilionCoordinator;
  deactivateWalk: () => void;
}

/**
 * Restart-failure handling, gameLaunched requeue, and realtime reconnect replay.
 */
export class MacroWalkRecoveryManager {
  public retryCount = 0;
  public retryCampaignId: string | null = null;
  private pendingGameLaunchedRequeue: (() => void) | null = null;
  private cancelRestartWait: (() => void) | null = null;
  private restartGeneration = 0;

  constructor(private readonly deps: MacroWalkRecoveryDeps) {}

  public clearRequeue(): void {
    if (this.pendingGameLaunchedRequeue) {
      try { this.pendingGameLaunchedRequeue(); } catch { /* ignore */ }
      this.pendingGameLaunchedRequeue = null;
    }
  }

  public cancelPendingRestart(): void {
    this.restartGeneration += 1;
    this.cancelRestartWait?.();
  }

  public resetRetries(): void {
    this.retryCount = 0;
    this.retryCampaignId = null;
  }

  public fallbackToLegacy(reason: string, options: { reportCampaignSwitchFailure?: boolean; campaignId?: string } = {}): void {
    console.log('[StatefulMacroSystemStep] ⤴️  Falling back to legacy onLaunchMacro path:', reason);
    Sentry.logger.warn(`[StatefulMacroSystemStep] Falling back to legacy onLaunchMacro path: ${reason}`);
    if (options.reportCampaignSwitchFailure && options.campaignId) {
      this.deps.gameLaunchCoordinator.reportCampaignSwitchFailure(options.campaignId, reason, false);
    }
    this.deps.deactivateWalk();
    this.resetRetries();
    void this.deps.gameLaunchCoordinator.executeLegacyOnLaunchMacro();
  }

  public async restartWait(payload: MacroWalkResponsePayload, reason: string): Promise<void> {
    await this.handleRestartFailure({
      ...payload, outcome: 'failure', nextNodeId: null, macros: [], captures: [],
      onFailure: 'restart', reason, waitPoll: undefined,
    });
  }

  public armGameLaunchedRequeue(
    campaignId: string,
    subscribeState: (cb: (gameLaunched: boolean) => void) => () => void,
    onReady: () => void,
  ): void {
    if (this.pendingGameLaunchedRequeue) {
      console.log('[StatefulMacroSystemStep] handleGameLaunched: requeue already pending — coalescing', {
        campaignId,
        retryCount: this.retryCount,
      });
      return;
    }

    console.warn(
      '[StatefulMacroSystemStep] handleGameLaunched: state.gameLaunched is false at handler entry — arming one-shot requeue',
      { campaignId, retryCount: this.retryCount, timeoutMs: GAME_LAUNCHED_REQUEUE_TIMEOUT_MS },
    );

    let settled = false;
    const cleanup = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      unsubscribe();
      this.pendingGameLaunchedRequeue = null;
    };

    const unsubscribe = subscribeState((gameLaunched) => {
      if (settled || !gameLaunched) return;
      cleanup();
      console.log('[StatefulMacroSystemStep] handleGameLaunched: state.gameLaunched flipped to true — re-running handler', {
        campaignId,
      });
      onReady();
    });

    const timer = setTimeout(() => {
      if (settled) return;
      cleanup();
      console.warn(
        '[StatefulMacroSystemStep] handleGameLaunched: requeue timed out without state.gameLaunched=true — dropping walk attempt',
        { campaignId, retryCount: this.retryCount, timeoutMs: GAME_LAUNCHED_REQUEUE_TIMEOUT_MS },
      );
    }, GAME_LAUNCHED_REQUEUE_TIMEOUT_MS);

    this.pendingGameLaunchedRequeue = cleanup;
  }

  public async handleRestartFailure(payload: MacroWalkResponsePayload): Promise<void> {
    this.retryCount += 1;
    this.retryCampaignId = payload.campaignId;
    const restartContext = {
      retryCount: this.retryCount,
      max: MACRO_WALK_MAX_RETRIES,
      reason: payload.reason,
      campaignId: payload.campaignId,
      launchInstanceId: payload.launchInstanceId,
    };
    console.warn('[StatefulMacroSystemStep] 🔁 restart triggered', restartContext);
    Sentry.logger.warn('[StatefulMacroSystemStep] 🔁 restart triggered', restartContext);

    if (this.retryCount > MACRO_WALK_MAX_RETRIES) {
      const reason = payload.reason && payload.reason !== 'walk_failure'
        ? payload.reason
        : 'macro_walk_max_retries';
      const escalationContext = { ...restartContext, escalationReason: reason };
      console.error(
        '[StatefulMacroSystemStep] ❌ macro walk max retries exceeded — escalating campaign failure',
        escalationContext,
      );
      Sentry.logger.error(
        '[StatefulMacroSystemStep] ❌ macro walk max retries exceeded — escalating campaign failure',
        escalationContext,
      );

      this.deps.deactivateWalk();
      this.deps.gameLaunchCoordinator.stopGame(this.deps.pavilionCoordinator.getAuthId());
      this.deps.gameLaunchCoordinator.reportCampaignSwitchFailure(
        payload.campaignId,
        reason,
        false,
      );
      this.resetRetries();
      return;
    }

    this.deps.deactivateWalk();

    const authId = this.deps.pavilionCoordinator.getAuthId();

    this.cancelPendingRestart();
    const restartGeneration = this.restartGeneration;
    const gameClosedPromise = new Promise<'closed' | 'timeout' | 'cancelled'>((resolve) => {
      let settled = false;
      const finish = (outcome: 'closed' | 'timeout' | 'cancelled') => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        unsubscribe();
        this.cancelRestartWait = null;
        resolve(outcome);
      };
      const unsubscribe = this.deps.gameLaunchCoordinator.events.gameClosed.addListener(() => finish('closed'));
      const timer = setTimeout(() => finish('timeout'), RESTART_GAMECLOSED_TIMEOUT_MS);
      this.cancelRestartWait = () => finish('cancelled');
    });

    this.deps.pavilionCoordinator.beginMacroRetryRestart();
    this.deps.gameLaunchCoordinator.stopGame(authId);

    const closeOutcome = await gameClosedPromise;
    if (closeOutcome === 'cancelled' || restartGeneration !== this.restartGeneration) return;
    if (closeOutcome === 'timeout') {
      console.warn(
        '[StatefulMacroSystemStep] ⏱ gameClosed not observed before timeout — relaunching anyway',
        { ...restartContext, timeoutMs: RESTART_GAMECLOSED_TIMEOUT_MS },
      );
      Sentry.logger.warn(
        '[StatefulMacroSystemStep] ⏱ gameClosed not observed before timeout — relaunching anyway',
        { ...restartContext, timeoutMs: RESTART_GAMECLOSED_TIMEOUT_MS },
      );
    } else {
      console.log('[StatefulMacroSystemStep] gameClosed observed — issuing relaunch', restartContext);
      Sentry.logger.info('[StatefulMacroSystemStep] gameClosed observed — issuing relaunch', restartContext);
    }

    this.deps.pavilionCoordinator.events.campaignConfigLoaded.emit({ campaignId: payload.campaignId });
  }
}
